export const CORE_ANALYTICS_EVENTS = Object.freeze([
  'app_opened',
  'navigation_selected',
  'resume_selected',
  'reader_content_visible',
  'reading_session_started',
  'reading_session_completed',
  'note_capture_started',
  'note_saved_local',
  'note_synced',
  'note_save_failed',
  'book_search_used',
  'book_filter_applied',
  'upload_started',
  'upload_completed',
  'ai_action_started',
  'ai_output_generated',
  'ai_output_saved',
  'ai_output_edited',
  'ai_feedback_submitted'
]);

// Values for these keys must be controlled metadata, never user-authored text.
const SAFE_PROPERTY_KEYS = new Set([
  'category', 'action', 'source', 'surface', 'destination', 'route_template',
  'previous_route_template', 'book_format', 'capture_method', 'has_active_book',
  'has_source_link', 'save_target', 'sync_state', 'filter_type', 'active_filter_count',
  'query_length', 'result_count', 'upload_source', 'file_type', 'file_size_bucket',
  'ai_action', 'context_type', 'output_type', 'was_edited', 'feedback_value',
  'duration_ms', 'duration_seconds', 'duration_minutes', 'page_count', 'count',
  'value', 'delta', 'rating', 'metric_name', 'name', 'status', 'outcome',
  'error_code', 'platform', 'app_version', 'environment', 'viewport_bucket',
  'analytics_enabled', 'crash_reporting_enabled', 'feature_name', 'step',
  'flag_name', 'flag_variant', 'rollout_percentage', 'session_id', 'timestamp',
  'test', 'sentry_id', 'service_type', 'request_size', 'interaction_type',
  'engagement_time', 'achievement_type', 'points_earned', 'level',
  'challenge_id', 'load_time', 'dom_ready', 'total_time', 'transfer_size'
]);

const PRIVATE_KEY_PATTERN = /(book_title|author|filename|file_name|note|content|text|quote|prompt|search_term|query|tag|summary|description|error_message|error_stack|stack|referrer|full_url|page_url|user_agent)/i;
const CONTROLLED_STRING_PATTERN = /^[a-zA-Z0-9_./:+-]{1,96}$/;

export const normalizeRouteTemplate = (pathname = '/') => {
  const cleanPath = String(pathname).split(/[?#]/)[0] || '/';
  if (/^\/read\/[^/]+\/?$/.test(cleanPath)) return '/read/:bookId';
  if (/^\/reading\/[^/]+\/?$/.test(cleanPath)) return '/reading/:bookId';
  return cleanPath
    .split('/')
    .map((segment) => (/^[0-9a-f]{8}-[0-9a-f-]{27,}$/i.test(segment) || /^\d{4,}$/.test(segment) ? ':id' : segment))
    .join('/') || '/';
};

const sanitizeValue = (value) => {
  if (typeof value === 'boolean') return value;
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (typeof value !== 'string') return undefined;
  const trimmed = value.trim();
  return CONTROLLED_STRING_PATTERN.test(trimmed) ? trimmed : undefined;
};

export const sanitizeAnalyticsProperties = (properties = {}) => Object.fromEntries(
  Object.entries(properties).flatMap(([key, value]) => {
    if (!SAFE_PROPERTY_KEYS.has(key) || PRIVATE_KEY_PATTERN.test(key)) return [];
    const sanitized = sanitizeValue(value);
    return sanitized === undefined ? [] : [[key, sanitized]];
  })
);

export const sanitizeAnalyticsEventName = (eventName) => {
  const normalized = String(eventName || '').trim().toLowerCase();
  return /^[a-z][a-z0-9_]{1,63}$/.test(normalized) ? normalized : null;
};

export const createAnalyticsEvent = (eventName, properties = {}, standard = {}) => {
  const event = sanitizeAnalyticsEventName(eventName);
  if (!event) return null;
  return {
    event,
    timestamp: Number.isFinite(standard.timestamp) ? standard.timestamp : Date.now(),
    session_id: standard.sessionId,
    ...sanitizeAnalyticsProperties(properties),
    app_version: standard.appVersion,
    environment: standard.environment,
    platform: standard.platform,
    route_template: normalizeRouteTemplate(standard.pathname)
  };
};
